<template>
  <Dialog
    header="Session Expiration"
    class="p-dialog-skinny"
    :visible="finalCountdown.isEnabled"
    @update:visible="extendSession()"
  >
    <p>
      You will be signed out in
      <span v-if="finalCountdown.currentSeconds > 60">{{ finalCountdown.currentMinutes }}&nbsp;minute<span v-if="finalCountdown.currentMinutes !== 1">s</span>.</span>
      <span v-if="finalCountdown.currentSeconds <= 60">{{ finalCountdown.currentSeconds }}&nbsp;second<span v-if="finalCountdown.currentSeconds !== 1">s</span>.</span>
    </p>
    <p>Would you like to extend your session?</p>
    <template #footer>
      <Button
        label="Sign Out"
        icon="material-symbols-outlined logout"
        class="p-button-text p-button-rounded"
        @click="router.push('/signed-out')"
      />
      <Button
        label="Extend Session"
        icon="material-symbols-outlined check"
        class="p-button-rounded"
        @click="[ finalCountdown.isEnabled = false, extendSession() ]"
      />
    </template>
  </Dialog>
</template>

<script setup>

  import { ref, inject, computed, watch } from "vue";
  import { useRouter } from "vue-router";
  import { useIdle, useTimestamp, useDocumentVisibility } from "@vueuse/core";
  import axios from "axios";
  import AuthenticationService from "@/services/AuthenticationService.js";
  const appState = inject("appState");
  const router = useRouter();

  // to check to see if the browser tab is visible or running in the background
  const browserTabVisibility = useDocumentVisibility();

  // this sets idleSeconds to the number of seconds the user has been idle
  const { lastActive } = useIdle();
  const idleSeconds = computed(() => Math.floor((useTimestamp().value - lastActive.value) / 1000));

  // settings for dialog that comes up
  const finalCountdown = ref();
  const finalCountdownInitialSeconds = 300;

  // set finalCountdown values to initial setup... this happens on init and when the user extends their session
  function resetFinalCountdown() {
    finalCountdown.value = {
      currentSeconds: 0,
      currentMinutes: computed(() => Math.ceil(finalCountdown.value.currentSeconds / 60)),
      isEnabled: false
    }
  }
  resetFinalCountdown();

  // this fires immediately, when the user clicks "extend session", and when/if the user has signed out and back in from another tab
  function setupSessionTimer() {
    
    // if the token still has time remaining, but it is less than finalCountdownInitialSeconds (5 minutes), auto-extend session and return
    if (AuthenticationService.secondsUntilAccessTokenExpires() < finalCountdownInitialSeconds) { extendSession(); return; }

    // save the value of localStorage.getItem("authTokens").expiration_relative_to_system_date_time to ensure it hasn't changed once the setTimeout function is executed
    const initialExpirationRelativeToSystemDateTime = JSON.parse(localStorage.getItem("authTokens")).expiration_relative_to_system_date_time;

    setTimeout(async () => { 

      // this checks that the user hasn't signed out (and not signed back in) to the portal from another tab
      if (!localStorage.getItem("authTokens")) { router.push("/session-expired"); return; }

      // this checks that the user hasn't signed out and then signed back in to the portal from another tab
      // if so, re-initializing this function will get everything back in sync with the new values in localStorage
      if (JSON.parse(localStorage.getItem("authTokens")).expiration_relative_to_system_date_time !== initialExpirationRelativeToSystemDateTime) { setupSessionTimer(); return; }

      // if the user has been idle for less than 5 minutes or the browser tab is currently visible, extend session
      if (idleSeconds.value < 300 || browserTabVisibility.value === "visible") { extendSession(); return; }

      // if the user is assigned to allOrganizationCodes and their ip address is in the north oak office, extend session
      let isUserAllOrganizationCodesAndInNorthOakOffice = false;
      if (appState.user.allOrganizationCodes) {
        const clientIpAddress = await axios.get("https://api.ipify.org");
        isUserAllOrganizationCodesAndInNorthOakOffice = clientIpAddress?.data === "138.199.99.26";
      }
      if (isUserAllOrganizationCodesAndInNorthOakOffice) { extendSession(); return; }

      // otherwise, start THE FINAL COUNTDOWN and pop open the "session is about to expire" dialog
      finalCountdown.value.isEnabled = true;
      finalCountdown.value.currentSeconds = finalCountdownInitialSeconds;

    }, (AuthenticationService.secondsUntilAccessTokenExpires() - finalCountdownInitialSeconds) * 1000);
    
  }
  setupSessionTimer();

  // this essentially counts down the seconds that are displayed on the dialog, and redirects to /session-expired when seconds <= 0
  watch(() => finalCountdown.value.currentSeconds, (val) => {
    if (finalCountdown.value.isEnabled) {
      if (val > 0) {
        setTimeout(() => { finalCountdown.value.currentSeconds = AuthenticationService.secondsUntilAccessTokenExpires()}, 1000);
      } else {
        router.push("/session-expired");
      }
    }
  });

  // if the user closes the dialog or clicks "extend session" button, reset finalCountdown values, hide dialog, refresh token, and set up session timer
  function extendSession() {
    resetFinalCountdown();
    AuthenticationService.getTokensFromRefreshToken(JSON.parse(localStorage.getItem("authTokens")).refresh_token).then(() => setupSessionTimer());
  }

</script>